// Stumble Cam orchestrator — Plan v2 G4 / Wave 3.
//
// Pipes a user-supplied Blob through the full Huji-style filter stack and
// returns the final encoded image as a Blob ready for upload.
//
// Pipeline:
//   1. Compress (longest edge ≤ maxDimension, EXIF stripped via re-render)
//   2. Decode the compressed blob back to an ImageBitmap, paint to a fresh
//      working canvas (compressImage doesn't expose its internal canvas).
//   3. Apply the filter stack in fixed order — order matters for the look:
//        coolCast → liftedBlacks → contrast → saturation → grain →
//        vignette → lightLeak → bloom → textOverlay → border
//      F-wave β recipe ("cool Polaroid, super saturated"):
//        - coolCast (Polaroid SX-70 cyan cast) replaces the previous warm
//          orange path; the old function lives on as a standalone export for
//          callers that need the disposable-camera look.
//        - bloom is the new always-on halation step, sits between lightLeak
//          and textOverlay so the leaks themselves bloom (highlight glow on
//          top of orange/pink streaks reads as warm Polaroid light).
//      Each filter is pure given (canvas, FilterContext.rng, palette), so the
//      same {photoId, palette, timestamp, ...inputs} always produces the same
//      output bytes. This is the invariant the recap render path relies on.
//   4. Re-encode the final canvas. If the result exceeds 1MB (G6 ceiling),
//      re-encode once at fallback quality 0.7.
//
// border() is special: when mode='polaroid' it allocates a NEW canvas at
// larger dimensions. We capture its return value and use the new canvas for
// the final export.

import { canvasToBlob, makeCanvas } from './canvasExport'
import type { FilterContext, Palette } from './filters/_helpers'
import { bloom } from './filters/bloom'
import { border } from './filters/border'
import { contrast } from './filters/contrast'
import { coolCast } from './filters/coolCast'
import { grain } from './filters/grain'
import { liftedBlacks } from './filters/liftedBlacks'
import { lightLeak } from './filters/lightLeak'
import { saturation } from './filters/saturation'
import { textOverlay } from './filters/textOverlay'
import { vignette } from './filters/vignette'
import { compressImage } from './imageCompression'
import { seededRandom } from './seededRandom'

export type StumbleCamOptions = {
  // Seed for any per-photo randomness (grain, lightLeak). Same photoId →
  // byte-identical output (server re-renders deterministically for the recap).
  photoId: string
  // Used by textOverlay for the date stamp.
  timestamp: Date
  // Optional textOverlay context.
  venueName?: string
  city?: string
  // Polaroid frame (used by share mode). Default 'none'.
  border?: 'none' | 'polaroid'
  // Compression knobs — defaults match Plan v2 G6.
  maxDimension?: number
  format?: 'image/webp' | 'image/jpeg'
  quality?: number
  // Drives grain amplitude (palettes have different --grain-opacity values).
  palette?: Palette
  // If true, also return the pre-filter compressed blob (used by SelfieSheet
  // to keep an unfiltered preview alongside the upload).
  keepOriginal?: boolean
}

export type StumbleCamResult = {
  filtered: Blob
  original?: Blob
  width: number
  height: number
  durationMs: number
}

const DEFAULT_MAX_DIMENSION = 1280
const DEFAULT_FORMAT = 'image/webp' as const
const DEFAULT_QUALITY = 0.78
const DEFAULT_PALETTE: Palette = 'poster'
const SIZE_CAP_BYTES = 1_000_000
const FALLBACK_QUALITY = 0.7

export async function processStumblePhoto(
  input: Blob,
  options: StumbleCamOptions,
): Promise<StumbleCamResult> {
  const start = performance.now()

  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION
  const format = options.format ?? DEFAULT_FORMAT
  const quality = options.quality ?? DEFAULT_QUALITY
  const palette = options.palette ?? DEFAULT_PALETTE
  const borderMode = options.border ?? 'none'

  // Step 1 — compress. This also strips EXIF as a side-effect of re-rendering.
  const compressed = await compressImage(input, { maxDimension, format, quality })

  // Step 2 — decode the compressed blob onto a fresh working canvas. We can't
  // reuse compressImage's internal canvas (it's not exposed), and decoding
  // through createImageBitmap is the cheapest way to get pixels back.
  const bitmap = await createImageBitmap(compressed)
  try {
    const width = bitmap.width
    const height = bitmap.height

    const workCanvas = makeCanvas(width, height)
    const workCtx = workCanvas.getContext('2d') as
      | CanvasRenderingContext2D
      | OffscreenCanvasRenderingContext2D
      | null
    if (!workCtx) {
      throw new Error('processStumblePhoto: 2d context unavailable on working canvas')
    }
    workCtx.drawImage(bitmap, 0, 0)

    const context: FilterContext = {
      ctx: workCtx,
      width,
      height,
      rng: seededRandom(options.photoId),
      palette,
    }

    // Step 3 — filter stack. Order is part of the cool-Polaroid "look" and
    // must not change without re-blessing every visual golden. bloom sits
    // AFTER lightLeak so the warm orange/pink streaks themselves halo,
    // reinforcing the "warm light at night" reading; BEFORE textOverlay so
    // the timestamp text stays crisp instead of glowing.
    coolCast(context)
    liftedBlacks(context)
    contrast(context)
    saturation(context)
    grain(context)
    vignette(context)
    lightLeak(context)
    bloom(context)
    textOverlay(context, {
      timestamp: options.timestamp,
      venueName: options.venueName,
      city: options.city,
    })

    // border() may return a NEW canvas at larger dimensions (polaroid mode).
    // Switch to it for the final export.
    const framed = border(context, { mode: borderMode })

    // Step 4 — encode. If first pass exceeds the 1MB ceiling, re-encode once
    // at fallback quality (matches compressImage's policy).
    const finalCanvas = framed.ctx.canvas as OffscreenCanvas | HTMLCanvasElement
    let filtered = await canvasToBlob(finalCanvas, format, quality)
    if (filtered.size > SIZE_CAP_BYTES) {
      filtered = await canvasToBlob(finalCanvas, format, FALLBACK_QUALITY)
    }

    const result: StumbleCamResult = {
      filtered,
      width: framed.width,
      height: framed.height,
      durationMs: performance.now() - start,
    }
    if (options.keepOriginal) {
      result.original = compressed
    }
    return result
  } finally {
    bitmap.close()
  }
}
